import React from 'react';
import PropTypes from 'prop-types';

const STATUS = {
  error: { label: 'Error', cls: 'tw-bg-red-50 tw-text-red-700' },
  changed: { label: 'Update', cls: 'tw-bg-emerald-50 tw-text-emerald-700' },
  created: { label: 'Add', cls: 'tw-bg-primary-50 tw-text-primary-700' },
};

const blank = value => (value === null || value === undefined || value === '' ? '—' : String(value));

// Rows with nothing to change are left out: on a 300-row template the useful
// part is the handful that change or fail, not 290 lines saying "no change".
const ImportResultTable = ({ rows }) => {
  const visible = rows.filter(row => row.status !== 'unchanged');
  if (visible.length === 0) return null;

  return (
    <div className="tw-overflow-x-auto tw-border tw-border-solid tw-border-slate-200 tw-rounded">
      <table className="tw-w-full tw-text-xs">
        <thead className="tw-bg-slate-50 tw-text-slate-500">
          <tr>
            <th scope="col" className="tw-text-left tw-font-semibold tw-px-2 tw-py-1.5">Row</th>
            <th scope="col" className="tw-text-left tw-font-semibold tw-px-2 tw-py-1.5">Shipment</th>
            <th scope="col" className="tw-text-left tw-font-semibold tw-px-2 tw-py-1.5">Status</th>
            <th scope="col" className="tw-text-left tw-font-semibold tw-px-2 tw-py-1.5">Details</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(row => (
            <tr key={row.row} className="tw-border-0 tw-border-t tw-border-solid tw-border-slate-100 tw-align-top">
              <td className="tw-px-2 tw-py-1.5 tw-tabular-nums tw-text-slate-500">{row.row}</td>
              <td className="tw-px-2 tw-py-1.5 tw-whitespace-nowrap">
                <div className="tw-font-semibold tw-text-slate-700">{blank(row.job_number)}</div>
                <div className="tw-text-slate-500">{[row.building, row.phase_name].filter(Boolean).join(' · ')}</div>
              </td>
              <td className="tw-px-2 tw-py-1.5">
                <span className={`tw-inline-block tw-rounded tw-px-1.5 tw-py-0.5 tw-font-semibold ${STATUS[row.status]?.cls}`}>
                  {STATUS[row.status]?.label}
                </span>
              </td>
              <td className="tw-px-2 tw-py-1.5">
                <ul className="tw-mb-0 tw-pl-0 tw-list-none tw-flex tw-flex-col tw-gap-0.5">
                  {row.errors.map(message => <li key={message} className="tw-text-red-600">{message}</li>)}
                  {row.changes.map(change => (
                    <li key={change.field} className="tw-text-slate-600">
                      <span className="tw-font-medium">{change.field}:</span>{' '}
                      <span className="tw-text-slate-400 tw-line-through">{blank(change.from)}</span>{' → '}
                      <span className="tw-text-slate-800">{blank(change.to)}</span>
                    </li>
                  ))}
                  {row.warnings.map(message => <li key={message} className="tw-text-amber-600">{message}</li>)}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

ImportResultTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    row: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    job_number: PropTypes.string,
    building: PropTypes.string,
    phase_name: PropTypes.string,
    changes: PropTypes.array,
    errors: PropTypes.array,
    warnings: PropTypes.array,
  })).isRequired,
};

export default ImportResultTable;
